
import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/company`,
  url: `${siteUrl}/company`,
  name: '会社概要 | 株式会社鈴電エンジニア',
  description:
    '株式会社鈴電エンジニアの会社概要ページです。代表メッセージ、会社情報、沿革をご覧いただけます。',
  inLanguage: 'ja',
  isPartOf: { '@id': `${siteUrl}/#website` },
  about: { '@id': `${siteUrl}/#organization` },
};

export default function CompanyPage() {
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });
  const greetingAnimation = useScrollAnimation({ threshold: 0.2 });
  const infoAnimation = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO meta
    document.title = '会社概要 | 株式会社鈴電エンジニア';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアの会社概要ページです。代表メッセージ、会社情報、沿革をご覧いただけます。');

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-company';
    script.text = JSON.stringify(schemaJsonLd);
    document.head.appendChild(script);

    return () => {
      document.title = '株式会社鈴電エンジニア';
      if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアは、電気設備工事を中心に、空調・消防設備工事など、建物に関わる各種設備工事を行っています。');
      const el = document.getElementById('schema-company');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/e8245452d17263d2b959491ef21cedc8.jpeg"
            alt="会社概要"
            className="w-full h-full object-contain object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>
        <div 
          ref={heroAnimation.ref as React.RefObject<HTMLDivElement>}
          className={`relative z-10 text-center text-white px-6 scroll-animate ${heroAnimation.isVisible ? 'is-visible' : ''}`}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">会社概要</h1>
        </div>
      </section>

      {/* Message from President */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div 
            ref={greetingAnimation.ref as React.RefObject<HTMLDivElement>}
            className={`scroll-animate ${greetingAnimation.isVisible ? 'is-visible' : ''}`}
          >
            <div className="max-w-4xl mx-auto">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">
                  代表メッセージ
                </h2>
                <div className="w-full max-w-md h-0.5 bg-blue-600 mb-8 mx-auto"></div>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 sm:p-12 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                  <img
                    src="https://static.readdy.ai/image/0947c5c75234eb0df91750a621813a72/9e07b5b9673a019b56521029222db271.jpeg"
                    alt="Background Logo"
                    className="w-64 h-64 object-contain"
                  />
                </div>
                
                <div className="relative z-10">
                  <p className="text-base sm:text-lg leading-relaxed text-gray-700 mb-8">
                    株式会社 鈴電エンジニアのホームページをご覧いただき、誠にありがとうございます。
                  </p>
                  <p className="text-base sm:text-lg leading-relaxed text-gray-700 mb-8">
                    当社は、一般木造住宅から店舗・ビルに至るまで、内線電気設備工事を主軸として事業を展開してまいりました。現在では、その技術と経験を活かし、空調設備、太陽光発電設備、消防設備の保守点検など、幅広い分野の業務にも取り組んでいます。
                  </p>
                  <p className="text-base sm:text-lg leading-relaxed text-gray-700 mb-8">
                    また、従業員が安心して長く働ける会社を目指し、働きやすい職場環境の整備にも力を入れています。
                  </p>
                  <p className="text-base sm:text-lg leading-relaxed text-gray-700 mb-12">
                    当社は、こうした時代の流れに柔軟に対応しながら、電気設備工事を通じて地域社会に貢献できる企業であり続けることを使命としています。
                  </p>
                  <p className="text-base sm:text-lg leading-relaxed text-gray-700 mb-12">
                    今後とも、皆様のご期待に応えられるよう尽力してまいりますので、何卒よろしくお願い申し上げます。
                  </p>
                  <div className="text-right">
                    <p className="text-base sm:text-lg font-semibold text-gray-900 mb-1">株式会社 鈴電エンジニア</p>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">代表取締役 鈴木 保則</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div 
            ref={infoAnimation.ref as React.RefObject<HTMLDivElement>}
            className={`scroll-animate ${infoAnimation.isVisible ? 'is-visible' : ''}`}
          >
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">■ 会社概要</h2>
                <div className="space-y-4">
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">会社名</div>
                    <div className="flex-1 text-gray-900 text-sm">株式会社 鈴電エンジニア</div>
                  </div>
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">所在地</div>
                    <div className="flex-1 text-gray-900 text-sm">
                      〒960-0501<br />
                      福島県伊達市伏黒字上ケ戸71-6
                    </div>
                  </div>
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">設立年月日</div>
                    <div className="flex-1 text-gray-900 text-sm">昭和54年 1月13日</div>
                  </div>
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">事業内容</div>
                    <div className="flex-1 text-gray-900 text-sm">
                      電気設備工事 / 消防設備工事・保守点検 / 空調設備工事 / 管工事 / 電気通信工事
                    </div>
                  </div>
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">資本金</div>
                    <div className="flex-1 text-gray-900 text-sm">2,000万円</div>
                  </div>
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">建設業許可</div>
                    <div className="flex-1 text-gray-900 text-sm">
                      電気工事業 福島県知事 許可( 特-4 )第14067号<br />
                      消防施設工事業 福島県知事 許可( 般-4 )第14067号<br />
                      管工事業 福島県知事 許可( 般-4 )第14067号<br />
                      電気通信工事業 福島県知事 許可( 般-4 )第14067号
                    </div>
                  </div>
                  <div className="flex border-b border-gray-200 pb-4">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">登録電気工事業者</div>
                    <div className="flex-1 text-gray-900 text-sm">福島県知事届出 第54038号</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 flex-shrink-0 font-semibold text-gray-700 text-sm">
                      建設業新分野進出<br />企業認定
                    </div>
                    <div className="flex-1 text-gray-900 text-sm">
                      太陽光発電システムによる発電及び電力の売買<br />福島県知事 認定 第183号
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">■ 沿革</h2>
                <div className="space-y-4">
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">昭和42年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">自営の電気工事業を始める</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">昭和46年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">東北電力認定店となる</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">昭和49年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">建設業許可取得</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">昭和54年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">有限会社鈴木電設 法人化 資本金250万円</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">昭和56年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">資本金 500万円に増資</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">平成元年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">資本金1,000万円に増資</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">平成9年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">株式会社 鈴電エンジニアと社名変更</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">平成29年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">資本金2,000万円に増資</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">令和3年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">建設業新分野進出企業認定</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">令和5年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">福島県空気のきれいな施設認証</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">令和5年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">イクボス宣言実施</p></div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-28 flex-shrink-0 font-bold text-blue-600 text-base">令和6年</div>
                    <div className="flex-1"><p className="text-gray-900 text-sm">「仕事と生活の調和」推進企業認証</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
